import { ethers } from 'ethers';
import type {
  CryptoAssetBalanceParams,
  CryptoAssetBalanceResult,
  CryptoAssetEstimateGasParams,
  CryptoAssetGasEstimate,
  CryptoAssetMaxSendAmountParams,
  CryptoAssetMaxSendAmountResult,
  CryptoAssetTransactionHistoryItem,
  CryptoAssetTransactionHistoryResult,
  CryptoAssetTransactionHistoryParams,
  CryptoAssetSendParams,
  CryptoAssetTransactionResult,
  ICryptoAssetProvider,
} from './ICryptoAssetProvider';
import type { CryptoAsset } from '@/types/WalletTypes';
import type {
  ExplorerTransaction,
  ExplorerTransactionHistoryResponse,
  WalletServiceClient,
} from '@/resources/wallet-service.client';
import {
  bufferGasValue,
  createEthersRpcProviderResolver,
  emptyTransactionHistory,
  getSameFeeAssetMaxSendAmount,
  requireCryptoAssetChainId,
  requireMnemonic,
} from './shared';
import { getEvmTxnContext, resolveNativeSendValue } from './EvmCryptoAssetProvider';

export class EvmLayer2NativeCryptoAssetProvider implements ICryptoAssetProvider {
  readonly name = 'evm-layer2-native';

  private readonly getRpcProvider = createEthersRpcProviderResolver();

  constructor(private readonly walletServiceClient: WalletServiceClient) {}

  supports(cryptoAsset: CryptoAsset): boolean {
    return cryptoAsset.traits.isEvm && cryptoAsset.traits.isNative && cryptoAsset.traits.isDefaultLayer2;
  }

  validateAddress(address: string): boolean {
    return ethers.utils.isAddress(address);
  }

  async getTransactionHistory(
    params: CryptoAssetTransactionHistoryParams
  ): Promise<CryptoAssetTransactionHistoryResult> {
    const { cryptoAsset } = params;
    if (!cryptoAsset.asset.blockchainConfiguration.blockExplorerApiUrl) {
      throw new Error('Layer 2 transaction history requires a block explorer API URL');
    }
    const transactionHistoryUrl = buildExplorerAccountUrl(
      cryptoAsset.asset.blockchainConfiguration.blockExplorerApiUrl,
      'txlist',
      params.address
    );
    const internalTransactionHistoryUrl = buildExplorerAccountUrl(
      cryptoAsset.asset.blockchainConfiguration.blockExplorerApiUrl,
      'txlistinternal',
      params.address
    );

    const [transactionData, internalTransactionData] = await Promise.all([
      this.walletServiceClient.getPrivateTokenTransactionHistory({
        page: 0,
        pageSize: EXPLORER_TRANSACTION_FETCH_SIZE,
        sortBy: params.sortBy,
        sortDir: 'DESC',
        url: transactionHistoryUrl,
      }),
      this.walletServiceClient
        .getPrivateTokenTransactionHistory({
          page: 0,
          pageSize: EXPLORER_TRANSACTION_FETCH_SIZE,
          sortBy: params.sortBy,
          sortDir: 'DESC',
          url: internalTransactionHistoryUrl,
        })
        .catch(() => ({ result: [] })),
    ]);
    const results = [
      ...normalizeExplorerTransactions(transactionData).map((txn) => ({
        ...txn,
        transactionType: undefined,
      })),
      ...normalizeExplorerTransactions(internalTransactionData).map((txn) => ({
        ...txn,
        transactionType: 'Internal',
      })),
    ].sort((a, b) => {
      const direction = params.sortDir?.toUpperCase() === 'ASC' ? 1 : -1;
      return (Number(a.timeStamp || 0) - Number(b.timeStamp || 0)) * direction;
    });
    if (!results.length) {
      return emptyTransactionHistory(params.page);
    }
    const transactions = results.map((txn): CryptoAssetTransactionHistoryItem => {
      const from = txn.from || '';
      const to = txn.to || '';
      const hash = txn.hash || '';
      const isWithdrawal = from.toLowerCase() === params.address.toLowerCase();
      const transactionType = getExplorerTransactionType(txn, isWithdrawal);
      const amount = Number(txn.value || 0) / 10 ** cryptoAsset.display.decimalPrecision;
      const signedAmount = isWithdrawal ? -amount : amount;
      const fee = (Number(txn.gasUsed || 0) * Number(txn.gasPrice || 0)) / 10 ** cryptoAsset.fee.decimalPrecision;

      return {
        id: hash,
        status: isExplorerTransactionError(txn) ? 'Failed' : 'Complete',
        confirmations: txn.confirmations,
        timestamp: new Date(Number(txn.timeStamp || 0) * 1000).toISOString(),
        fee,
        link: cryptoAsset.asset.blockchainConfiguration.blockExplorerUrl
          ? `${cryptoAsset.asset.blockchainConfiguration.blockExplorerUrl}/tx/${hash}`
          : '',
        to: to ? [to] : [],
        from,
        amount: signedAmount,
        type: transactionType,
        total: signedAmount,
      };
    });
    const startIdx = params.page * params.pageSize;

    return {
      page: params.page,
      totalElements: transactions.length,
      totalPages: Math.ceil(transactions.length / params.pageSize),
      transactions: transactions.slice(startIdx, startIdx + params.pageSize),
    };
  }

  async getBalance({ address, cryptoAsset }: CryptoAssetBalanceParams): Promise<CryptoAssetBalanceResult> {
    if (!address) {
      return { balance: null, canSendFunds: false, hasError: true };
    }
    try {
      const l2Provider = this.getRpcProvider(cryptoAsset);
      const balance = await l2Provider.getBalance(address);

      return {
        balance: ethers.utils.formatUnits(balance, cryptoAsset.display.decimalPrecision),
        canSendFunds: true,
        hasError: false,
      };
    } catch {
      return { balance: null, canSendFunds: false, hasError: true };
    }
  }

  async estimateGas({ amount, core, to }: CryptoAssetEstimateGasParams): Promise<CryptoAssetGasEstimate> {
    const coin = await this.walletServiceClient.estimateLayer2Gas({ amount: Number(amount), core, to });
    return {
      coin,
      usd: 0,
    };
  }

  getMaxSendAmount(params: CryptoAssetMaxSendAmountParams): CryptoAssetMaxSendAmountResult {
    return getSameFeeAssetMaxSendAmount(params);
  }

  async send({
    amount,
    cryptoAsset,
    isMaxSend,
    mnemonic,
    privateKey,
    to,
  }: CryptoAssetSendParams): Promise<CryptoAssetTransactionResult> {
    const l2Provider = this.getRpcProvider(cryptoAsset);
    const wallet = privateKey
      ? new ethers.Wallet(privateKey).connect(l2Provider)
      : ethers.Wallet.fromMnemonic(requireMnemonic(mnemonic)).connect(l2Provider);

    return sendL2Txn({
      provider: l2Provider,
      cryptoAsset,
      to,
      amount,
      wallet,
      isMaxSend,
    });
  }
}

async function sendL2Txn({ provider, cryptoAsset, to, amount, wallet, isMaxSend }) {
  const txnContext = await getEvmTxnContext({
    provider,
    wallet,
  });
  const feeData = await provider.getFeeData();
  const gasPrice = feeData.gasPrice || ethers.utils.parseUnits('10', 'gwei');
  const requestedValue = ethers.utils.parseUnits(amount, cryptoAsset.display.decimalPrecision);
  const estimatedGasLimit = bufferGasValue(
    await provider.estimateGas({
      to,
      value: requestedValue,
      from: txnContext.connectedWallet.address,
    })
  );
  const fee = estimatedGasLimit.mul(gasPrice);
  const sendValue = await resolveNativeSendValue({
    fee,
    isMaxSend,
    provider,
    requestedValue,
    walletAddress: txnContext.connectedWallet.address,
  });

  return signAndSendL2Txn({
    provider,
    cryptoAsset,
    gasLimit: estimatedGasLimit,
    gasPrice,
    to,
    sendValue,
    txnContext,
  });
}

async function signAndSendL2Txn({ provider, cryptoAsset, gasLimit, gasPrice, sendValue, to, txnContext }) {
  const chainId = requireCryptoAssetChainId(cryptoAsset);
  const signedTx = await txnContext.connectedWallet.signTransaction({
    to,
    value: sendValue,
    nonce: txnContext.latestNonce,
    gasLimit,
    gasPrice,
    chainId,
    type: 0,
  });
  const txResponse = await provider.sendTransaction(signedTx);
  const receipt = await txResponse.wait(1);

  return {
    hash: receipt.transactionHash,
    success: receipt.status === 1,
    network: cryptoAsset.asset.blockchainConfiguration.name,
  };
}

type ExplorerTransactionWithType = ExplorerTransaction & {
  transactionType?: string;
};

const EXPLORER_TRANSACTION_FETCH_SIZE = 500;

const normalizeExplorerTransactions = (data: ExplorerTransactionHistoryResponse): ExplorerTransactionWithType[] =>
  Array.isArray(data?.result) ? data.result : [];

const buildExplorerAccountUrl = (baseUrl: string, action: string, address: string) => {
  const separator = baseUrl.includes('?') ? '&' : '?';
  const hasAction = /[?&]action=/.test(baseUrl);
  if (hasAction) {
    return baseUrl;
  }
  return `${baseUrl}${separator}module=account&action=${action}&address=${address}`;
};

const getExplorerTransactionType = (transaction: ExplorerTransactionWithType, isWithdrawal: boolean) => {
  const input = transaction.input || '';
  const isContractCall =
    transaction.transactionType === 'Internal' || (input !== '' && input !== '0x' && input !== '0x0');
  if (isContractCall) {
    return isWithdrawal ? 'Bridge' : 'Reward';
  }
  return isWithdrawal ? 'Withdrawal' : 'Deposit';
};

const isExplorerTransactionError = (transaction: ExplorerTransactionWithType) =>
  transaction.isError === '1' ||
  transaction.txreceipt_status === '0' ||
  transaction.status === 'error' ||
  transaction.status === 'failed';
